import { TaskRow } from '@agentic-flow-studio/shared/components/tasks/TaskRow';
import { formatRelativeTime } from '@agentic-flow-studio/shared/lib/relative-time';
import { cn } from '@agentic-flow-studio/shared/lib/utils';
import { Badge } from '@agentic-flow-studio/shared/ui/badge';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@agentic-flow-studio/shared/ui/card';
import {
  AlarmClock,
  Anchor,
  ArrowRight,
  CalendarClock,
  CheckSquare,
  MessageCircleQuestion,
  UserRoundPlus,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { buildDashboardActions } from '@/lib/dashboard-actions';
import { trpc } from '@/utils/trpc';

function LoadingCount() {
  return <div className="h-8 w-12 animate-pulse rounded bg-muted" />;
}

export default function Dashboard() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const enabled = !!workspaceId;

  const meQuery = trpc.auth.me.useQuery(undefined, { enabled });
  const contactsQuery = trpc.contacts.getAll.useQuery(undefined, { enabled });
  const tasksQuery = trpc.tasks.getAll.useQuery(undefined, { enabled });
  const statusesQuery = trpc.taskStatuses.list.useQuery(undefined, { enabled });
  const membersQuery = trpc.workspace.listMembers.useQuery(undefined, { enabled });
  const needsResponseQuery = trpc.dashboard.needsResponse.useQuery({ limit: 5 }, { enabled });
  const utils = trpc.useUtils();

  const markResponseRead = trpc.dashboard.markResponseRead.useMutation({
    onSuccess: () => utils.dashboard.needsResponse.invalidate(),
  });

  const tasks = tasksQuery.data ?? [];
  const contacts = contactsQuery.data ?? [];
  const statuses = statusesQuery.data?.items ?? [];
  const members = membersQuery.data ?? [];
  const currentUserId = meQuery.data?.uid;
  const nowSec = Math.floor(Date.now() / 1000);
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfTodaySec = Math.floor(startOfToday.getTime() / 1000);
  const { myOverdueTasks, dueTodayTasks, unassignedTasks, contactsNeedingAttention, nextActions } =
    buildDashboardActions({
      tasks,
      contacts,
      statuses,
      currentUserId,
      nowSec,
      startOfTodaySec,
    });

  const isLoading =
    meQuery.isLoading ||
    contactsQuery.isLoading ||
    tasksQuery.isLoading ||
    statusesQuery.isLoading ||
    needsResponseQuery.isLoading;

  const metrics = [
    {
      title: 'My Overdue Tasks',
      value: myOverdueTasks.length,
      description: 'Past due and assigned to you',
      icon: AlarmClock,
      href: '../tasks?view=list&tab=overdue',
      danger: myOverdueTasks.length > 0,
    },
    {
      title: 'Due Today',
      value: dueTodayTasks.length,
      description: 'Workspace tasks due today',
      icon: CalendarClock,
      href: '../tasks?view=assignee&timeline=today',
      danger: false,
    },
    {
      title: 'Unassigned Tasks',
      value: unassignedTasks.length,
      description: 'Work waiting for an owner',
      icon: UserRoundPlus,
      href: '../tasks?view=assignee&assignees=unassigned',
      danger: false,
    },
    {
      title: 'Contacts Needing Attention',
      value: contactsNeedingAttention.length,
      description: 'Overdue work, missing context, or stale',
      icon: Anchor,
      href: '#contacts-needing-attention',
      danger: false,
    },
    {
      title: 'Needs Your Response',
      value: needsResponseQuery.data?.total ?? 0,
      description: 'Unread mentions waiting for you',
      icon: MessageCircleQuestion,
      href: '#needs-your-response',
      danger: false,
    },
  ];

  return (
    <main className="flex-1 bg-slate-50/50 dark:bg-slate-950/50">
      <div className="container mx-auto max-w-7xl px-6 py-10">
        <div className="flex flex-col gap-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Action Center</h1>
            <p className="mt-1 text-muted-foreground">
              What needs attention now, whether it is yours or still needs an owner.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            {metrics.map((metric) => (
              <Link key={metric.title} to={metric.href} className="group">
                <Card
                  className={cn(
                    'h-full border-border/50 bg-card/70 transition-colors group-hover:border-primary/40',
                    metric.danger && 'border-destructive/40 bg-destructive/5'
                  )}
                >
                  <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium leading-snug">
                      {metric.title}
                    </CardTitle>
                    <metric.icon
                      className={cn(
                        'h-4 w-4 shrink-0 text-muted-foreground',
                        metric.danger && 'text-destructive'
                      )}
                    />
                  </CardHeader>
                  <CardContent>
                    <div className={cn('text-2xl font-bold', metric.danger && 'text-destructive')}>
                      {isLoading ? <LoadingCount /> : metric.value}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{metric.description}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <Card className="border-border/50">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Do Next</CardTitle>
                <CheckSquare className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent className="space-y-2">
                {isLoading ? (
                  <div className="h-28 animate-pulse rounded-lg bg-muted" />
                ) : nextActions.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    Nothing urgent is waiting. You are caught up.
                  </p>
                ) : (
                  nextActions
                    .slice(0, 6)
                    .map(({ task, reason }) => (
                      <TaskRow
                        key={task.id}
                        task={task}
                        statuses={statuses}
                        members={members}
                        secondaryText={reason}
                        onClick={() => navigate(`../tasks/${task.id}`)}
                      />
                    ))
                )}
              </CardContent>
            </Card>

            <Card id="contacts-needing-attention" className="border-border/50">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Contacts Needing Attention</CardTitle>
                <Anchor className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent className="space-y-2">
                {isLoading ? (
                  <div className="h-28 animate-pulse rounded-lg bg-muted" />
                ) : contactsNeedingAttention.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    Every contact has context and no overdue work.
                  </p>
                ) : (
                  contactsNeedingAttention.slice(0, 6).map(({ contact, reason, overdueCount }) => (
                    <Link
                      key={contact.id}
                      to={`../contacts/${contact.id}`}
                      className="flex items-center gap-3 rounded-lg border border-transparent p-3 transition-colors hover:border-border hover:bg-muted/50"
                    >
                      <div
                        className={cn(
                          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary',
                          overdueCount > 0 && 'bg-destructive/10 text-destructive'
                        )}
                      >
                        {(contact.displayName || contact.label).slice(0, 1).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {contact.displayName || contact.label}
                        </p>
                        <p
                          className={cn(
                            'text-xs text-muted-foreground',
                            overdueCount > 0 && 'text-destructive'
                          )}
                        >
                          {reason}
                        </p>
                      </div>
                      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    </Link>
                  ))
                )}
              </CardContent>
            </Card>

            <Card id="needs-your-response" className="border-border/50">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Needs Your Response</CardTitle>
                <MessageCircleQuestion className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent className="space-y-2">
                {isLoading ? (
                  <div className="h-28 animate-pulse rounded-lg bg-muted" />
                ) : !needsResponseQuery.data?.items.length ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    No unread mentions are waiting for you.
                  </p>
                ) : (
                  needsResponseQuery.data.items.map((item) => {
                    const target =
                      item.targetType === 'task'
                        ? `../tasks/${item.targetId}`
                        : item.targetType === 'contact'
                          ? `../contacts/${item.targetId}`
                          : null;
                    const content = (
                      <>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{item.label}</p>
                          <p className="truncate text-xs text-muted-foreground">{item.content}</p>
                        </div>
                        <Badge variant="secondary" className="shrink-0 text-[10px]">
                          {formatRelativeTime(item.createdAt * 1000)}
                        </Badge>
                      </>
                    );

                    return target ? (
                      <Link
                        key={item.id}
                        to={target}
                        onClick={() => markResponseRead.mutate({ id: item.id })}
                        className="flex items-center gap-3 rounded-lg border border-transparent p-3 transition-colors hover:border-border hover:bg-muted/50"
                      >
                        {content}
                      </Link>
                    ) : (
                      <Button
                        key={item.id}
                        variant="ghost"
                        className="h-auto w-full justify-start gap-3 p-3 text-left"
                        onClick={() => markResponseRead.mutate({ id: item.id })}
                      >
                        {content}
                      </Button>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </main>
  );
}
